import React, { useEffect } from "react";
import { Modal, Select, Spin } from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  CalendarOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { CustomIcons } from "../../assets";
import * as S from "./styles";

const { Option } = Select;

interface Company {
  id: string;
  companyName: string;
}

interface Invoice {
  invoiceNumber?: string;
  dateAdded?: string;
  updatedAt?: string;
}

interface CompanyInvoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCompany: Company | null;
  invoicesDataResponse: any;
  isLoadingInvoices: boolean;
  invoiceSearchValue: string;
  setInvoiceSearchValue: (value: string) => void;
  invoiceMonthFilter: string;
  setInvoiceMonthFilter: (value: string) => void;
  invoiceYearFilter: string;
  setInvoiceYearFilter: (value: string) => void;
  onViewInvoice: (invoice: Invoice) => void;
}

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CompanyInvoicesModal: React.FC<CompanyInvoicesModalProps> = ({
  isOpen,
  onClose,
  selectedCompany,
  invoicesDataResponse,
  isLoadingInvoices,
  invoiceSearchValue,
  setInvoiceSearchValue,
  invoiceMonthFilter,
  setInvoiceMonthFilter,
  invoiceYearFilter,
  setInvoiceYearFilter,
  onViewInvoice,
}) => {
  // Generate years from current year down to 2025
  const generateYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= 2025; year--) {
      years.push(year);
    }
    return years;
  };

  const years = generateYears();

  // Set default year to current year when modal opens
  useEffect(() => {
    if (isOpen && !invoiceYearFilter) {
      const currentYear = new Date().getFullYear().toString();
      setInvoiceYearFilter(currentYear);
    }
  }, [isOpen, invoiceYearFilter, setInvoiceYearFilter]);

  const handleDownloadInvoice = async (invoice: any) => {
    const pdfLink = invoice.pdfLink;
    if (!pdfLink) return;

    try {
      // Fetch the PDF
      const response = await fetch(pdfLink);
      const blob = await response.blob();

      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Invoice_${invoice.invoiceNumber || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Download failed:", error);
      // Fallback to opening in new tab if download fails
      window.open(pdfLink, "_blank");
    }
  };

  const renderInvoiceListContent = () => {
    if (isLoadingInvoices) {
      return (
        <S.CenteredSpinner style={{ padding: "40px" }}>
          <Spin size="large" />
        </S.CenteredSpinner>
      );
    }

    const hasNoInvoices =
      !invoicesDataResponse?.data?.data ||
      invoicesDataResponse.data.data.length === 0;

    if (hasNoInvoices) {
      return (
        <S.CenteredEmpty style={{ padding: "40px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
            }}
          >
            <CustomIcons.EmptyStateIcon
              width={347}
              height={220}
              svgColor="#D0D5DD"
            />
            <S.EmptyStateText>No Invoices Found</S.EmptyStateText>
          </div>
        </S.CenteredEmpty>
      );
    }

    return (
      <S.InvoiceListContent>
        {invoicesDataResponse.data.data.map((invoice: any, index: number) => {
          const invoiceDate = invoice.dateAdded
            ? new Date(invoice.dateAdded)
            : null;
          const formattedDate = invoiceDate
            ? `${invoiceDate.getDate()} ${invoiceDate.toLocaleDateString(
                "en-GB",
                {
                  month: "long",
                  year: "numeric",
                }
              )}`
            : "Date not available";

          return (
            <S.InvoiceItem key={invoice.invoiceNumber || index}>
              <S.InvoiceInfo>
                <S.InvoiceNumber>
                  Invoice #
                  {invoice.invoiceNumber || String(index + 1).padStart(2, "0")}
                </S.InvoiceNumber>
                <S.InvoiceDateContainer>
                  <CalendarOutlined
                    style={{
                      fontSize: "16px",
                      color: "#323232",
                    }}
                  />
                  <S.InvoiceDate>{formattedDate}</S.InvoiceDate>
                </S.InvoiceDateContainer>
              </S.InvoiceInfo>
              <S.InvoiceActionsContainer>
                <S.InvoiceActionButton
                  onClick={() => handleDownloadInvoice(invoice)}
                  title="Download Invoice"
                >
                  <DownloadOutlined />
                </S.InvoiceActionButton>
                <S.ViewInvoicesButton onClick={() => onViewInvoice(invoice)}>
                  View Invoice
                </S.ViewInvoicesButton>
              </S.InvoiceActionsContainer>
            </S.InvoiceItem>
          );
        })}
      </S.InvoiceListContent>
    );
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={650}
      closeIcon={
        <CloseOutlined style={{ fontSize: "20px", color: "#515151" }} />
      }
      styles={{
        body: {
          padding: 0,
          height: "100%",
          minHeight: "650px",
          maxHeight: "650px",
          display: "flex",
          flexDirection: "column",
        },
      }}
      wrapClassName="billing-invoices-modal-wrap"
      getContainer={false}
    >
      <S.ModalContentWrapper>
        {/* Header */}
        <S.ModalHeader>
          <S.ModalTitle>
            {selectedCompany?.companyName || "Company"}
          </S.ModalTitle>
        </S.ModalHeader>

        {/* Search and Filter */}
        <S.ModalSearchFilterContainer>
          <S.ModalSearchInput
            placeholder="Search"
            allowClear
            value={invoiceSearchValue}
            onChange={(e) => setInvoiceSearchValue(e.target.value)}
            prefix={
              <SearchOutlined style={{ fontSize: "18px", color: "#9b9b9b" }} />
            }
          />
          <S.ModalMonthSelect
            value={invoiceMonthFilter}
            onChange={(value) => setInvoiceMonthFilter(value as string)}
            suffixIcon={
              <S.CalendarIconWrapper>
                <CalendarOutlined />
              </S.CalendarIconWrapper>
            }
            optionLabelProp="label"
          >
            <Option value="All" label="Month: All">
              All
            </Option>
            {months.map((month) => (
              <Option key={month} value={month} label={`Month: ${month}`}>
                {month}
              </Option>
            ))}
          </S.ModalMonthSelect>
          <S.ModalYearSelect
            value={invoiceYearFilter}
            onChange={(value) => setInvoiceYearFilter(value as string)}
            suffixIcon={
              <S.CalendarIconWrapper>
                <CalendarOutlined />
              </S.CalendarIconWrapper>
            }
            optionLabelProp="label"
          >
            {years.map((year) => (
              <Option
                key={year}
                value={year.toString()}
                label={`Year: ${year}`}
              >
                {year}
              </Option>
            ))}
          </S.ModalYearSelect>
        </S.ModalSearchFilterContainer>

        {/* Results Count */}
        <S.ResultsText style={{ margin: "0 0 16px 0", textAlign: "left" }}>
          Results: {invoicesDataResponse?.data?.totalCount || 0}
        </S.ResultsText>

        {/* Invoices List */}
        <S.InvoiceListContainer>
          {renderInvoiceListContent()}
        </S.InvoiceListContainer>
      </S.ModalContentWrapper>
    </Modal>
  );
};

export default CompanyInvoicesModal;
